// Aba Diário (0.65). Pede a senha (ou o desbloqueio do app) UMA vez ao entrar; lá dentro não pede de novo.
// 0.66 (B1): pede de novo conforme Ajustes > Abas > Diário > "Pedir a senha de novo" (padrão: depois de 5 minutos fora).
// Galeria, câmera, permissões e a biometria não contam como sair. Depois de desbloquear, volta para a mesma tela.
// Exceção: os Projetos em Grupo não pedem a senha e são a única parte que pode ser compartilhada.
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Alert, BackHandler, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect, useIsFocused } from 'expo-router';
import { useDoc } from '../store/store';
import {
  abrirComCodigo, abrirDiario, abrirPeloAparelho, apagarDiario, criarDiario, definirSenhaDiario, descarregar, diarioConfigurado,
  marcarEntradaDiario, marcarSaidaDiario, modoDiario, mudarDiario, senhaValida, temChaveNoAparelho, temSenhaDiario, tipoSenhaDiario, useDiario,
  type TipoSenhaDiario,
} from '../store/diario';
import { useConfirmarIdentidade } from '../components/ConfirmarIdentidade';
import { AcoesItem } from '../components/Acoes';
import { Ordenavel } from '../components/Arrastar';
import { Btn, Chip, Field, Screen, Sheet, useVoltarAndroid } from '../components/ui';
import { C, alpha, useCores } from '../components/theme';
import { PASTAS, Corpo, dataLonga, type Parte } from '../components/diario/comum';
import { MeuDia } from '../components/diario/MeuDia';
import { ProjetoVida } from '../components/diario/ProjetoVida';
import { Habitos } from '../components/diario/Habitos';
import { QuadroSonhos } from '../components/diario/Sonhos';
import { RodaVida } from '../components/diario/Roda';
import { Cartas } from '../components/diario/Cartas';
import { Inspiracao } from '../components/diario/Inspiracao';
import { Conquistas } from '../components/diario/Conquistas';
import { ConfigDiario } from '../components/diario/ConfigDiario';
import { CampoSenhaDiario, CodigoRecuperacao, CriarSenhaDiario } from '../components/diario/Senha';
import Modulo from './Modulo';
import { today } from '../lib/date';
import { useBotaoNovo } from '../lib/botaoNovo';
import { oferecerDesfazer } from '../lib/desfazer';
import { t } from '../lib/i18n';
import { useCaixas } from '../lib/caixas';

/** item vindo de outra aba: "meta:id", "hab:id", "insp:id", "carta:id", "dia:AAAA-MM-DD", "rel:id" (relatório gerado, fica em Conquistas); sem prefixo = projeto em grupo. */
const PREFIXOS = ['meta:', 'hab:', 'insp:', 'carta:', 'dia:', 'rel:'];

export default function Diario({ item, onBack }: { item?: string; onBack: () => void }) {
  const { aberto } = useDiario();
  const cor = useCores().corDe('diario');
  const deGrupo = !!item && !PREFIXOS.some(p => item.startsWith(p));
  const [grupo, setGrupo] = useState(deGrupo);
  const [codigoNovo, setCodigoNovo] = useState<string | null>(null);
  const [jaAbriu, setJaAbriu] = useState(false);
  const [novaSenha, setNovaSenha] = useState(false);   // abriu pelo código de recuperação: criar uma senha nova
  useEffect(() => { if (aberto) setJaAbriu(true); }, [aberto]);
  useVoltarAndroid(novaSenha && aberto ? () => setNovaSenha(false) : undefined);   // 0.70 (texto 09, E4)
  // entrar e sair da tela do Diário (trocar de aba também é sair)
  useFocusEffect(useCallback(() => { marcarEntradaDiario(); return () => marcarSaidaDiario(); }, []));
  // texto 24 (1): antes, desbloquear o app estando no Diário (ou até 1 minuto antes de entrar) abria o Diário junto, sem
  // pedir nada — por isso no celular ele "entrava direto". Agora o Diário SEMPRE pede ao entrar (a senha do diário ou o
  // desbloqueio do celular, conforme "Como o diário abre"); depois vale o "Pedir a senha de novo".
  if (grupo) return <Modulo id="diario" item={deGrupo ? item : undefined} onBack={() => (deGrupo ? onBack() : setGrupo(false))} />;
  if (codigoNovo) return <CodigoRecuperacao codigo={codigoNovo} cor={cor} onPronto={() => setCodigoNovo(null)} />;
  if (novaSenha && aberto) return (
    <Screen title={t('Senha nova do diário')} onBack={() => setNovaSenha(false)}>
      <Text style={{ color: C.text, fontWeight: '700' }}>{t('Código aceito. Crie uma senha nova para o diário.')}</Text>
      <CriarSenhaDiario cor={cor} botao={t('Salvar a senha nova')} onPronta={async (sn, tp) => { if (await definirSenhaDiario(sn, tp)) { setNovaSenha(false); Alert.alert(t('Senha do diário trocada')); } }} />
    </Screen>
  );
  return (
    <View style={{ flex: 1 }}>
      {jaAbriu && <DiarioAberto cor={cor} travado={!aberto} item={deGrupo ? undefined : item} onBack={onBack} onGrupo={() => setGrupo(true)} />}
      {!aberto && (
        <View style={jaAbriu ? [StyleSheet.absoluteFill, { backgroundColor: C.bg }] : { flex: 1 }}>
          <Trava cor={cor} onBack={onBack} onGrupo={() => setGrupo(true)} onCriado={setCodigoNovo} onNovaSenha={() => setNovaSenha(true)} />
        </View>
      )}
    </View>
  );
}

/** Tela de senha (ou de criação do diário, na primeira vez). */
function Trava({ cor, onBack, onGrupo, onCriado, onNovaSenha }: { cor: string; onBack: () => void; onGrupo: () => void; onCriado: (codigo: string) => void; onNovaSenha: () => void }) {
  const dp = useDoc('diariop');
  const ident = useConfirmarIdentidade(cor);
  const configurado = diarioConfigurado();
  const [modo, setModo] = useState<'propria' | 'app'>(Platform.OS === 'web' ? 'propria' : 'app');
  const [s1, setS1] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [errou, setErrou] = useState(false);
  // 'aparelho': abrir pelo desbloqueio do app · 'senha': senha do diário · 'codigo': código de recuperação
  const [tela, setTela] = useState<'aparelho' | 'senha' | 'codigo'>(() =>
    dp.modo === 'app' && Platform.OS !== 'web' ? 'aparelho' : temSenhaDiario() ? 'senha' : 'codigo');
  const tipo = tipoSenhaDiario();

  // forma "a mesma do app e do celular": desbloqueio do app (biometria, PIN do aparelho ou senha do app) e a chave deste aparelho
  async function peloApp() {
    setMsg('');
    if (Platform.OS === 'web' || !(await temChaveNoAparelho())) {
      setTela(temSenhaDiario() ? 'senha' : 'codigo');
      setMsg(temSenhaDiario() ? t('Primeira vez neste dispositivo: digite a senha do diário ou use o código de recuperação.') : t('Primeira vez neste dispositivo: digite o código de recuperação do diário.'));
      return;
    }
    if (!(await ident.pedir(t('Abrir o Diário')))) return;
    if (!(await abrirPeloAparelho())) { setTela(temSenhaDiario() ? 'senha' : 'codigo'); setMsg(t('Não foi possível abrir com a chave deste dispositivo. Use a senha do diário ou o código de recuperação.')); }
  }
  useEffect(() => { if (configurado && dp.modo === 'app') peloApp(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function abrir(v = s1) {
    if (!v) return;
    setBusy(true); setMsg('');
    try { if (!(await abrirDiario(v))) { setMsg(tipo === 'padrao' ? t('Desenho incorreto') : t('Senha do diário incorreta.')); setErrou(true); setTimeout(() => setErrou(false), 900); } }
    finally { setBusy(false); setS1(''); }
  }
  async function pelaRecuperacao() {
    setBusy(true); setMsg('');
    try {
      const propria = modoDiario() === 'propria';
      if (!(await abrirComCodigo(s1))) { setMsg(t('Código de recuperação incorreto.')); return; }
      if (propria) onNovaSenha();   // abriu: com senha própria, cria uma senha nova (sem perder nada)
    } finally { setBusy(false); setS1(''); }
  }
  async function criar(senha?: string, tp?: TipoSenhaDiario) {
    setMsg(''); setBusy(true);
    try { onCriado(await criarDiario({ modo, senha, tipo: tp })); }
    catch (e: any) { setMsg(String(e?.message ?? e)); } finally { setBusy(false); }
  }
  /** Esqueceu a senha: primeiro o código de recuperação; por último, apagar o diário e começar de novo. */
  function esqueci() {
    setS1(''); setTela('codigo');
    setMsg(t('Use o código de recuperação que apareceu quando o diário foi criado. Sem a senha e sem o código, ninguém consegue abrir o diário, nem o suporte.'));
  }
  async function apagar() {
    const ok = await ident.pedir(t('Apagar o diário'), { titulo: t('Apagar o diário'), botao: t('Apagar'), perigo: true, semBiometriaDireta: true,
      texto: t('Para apagar, confirme com a senha do app, biometria ou PIN do dispositivo. Isso apaga o Meu Dia, metas, hábitos, cartas e fotos do diário. Projetos em Grupo continuam.') });
    if (!ok) return;
    await apagarDiario();
    setTela('senha'); setS1('');
    Alert.alert(t('Diário apagado'), t('Crie o diário de novo para começar.'));
  }

  let corpo: React.ReactNode;
  if (!configurado) corpo = (
    <View style={{ gap: 10 }}>
      <Text style={{ color: C.sub }}>{t('Tudo o que você escrever no diário fica criptografado neste dispositivo antes de ir para a sua conta. Ninguém mais consegue ler, nem o suporte.')}</Text>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('COMO O DIÁRIO ABRE')}</Text>
      {Platform.OS !== 'web' && (
        <Pressable onPress={() => setModo('app')} style={{ backgroundColor: modo === 'app' ? alpha(cor, 0.14) : C.card, borderRadius: 14, padding: 12, borderWidth: 1, borderColor: modo === 'app' ? cor : C.line, gap: 4 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>{modo === 'app' ? '◉' : '○'} {t('A mesma forma de desbloqueio do app e do celular')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Biometria, PIN do dispositivo ou a senha do app. Não precisa criar outra senha.')}</Text>
        </Pressable>
      )}
      <Pressable onPress={() => setModo('propria')} style={{ backgroundColor: modo === 'propria' ? alpha(cor, 0.14) : C.card, borderRadius: 14, padding: 12, borderWidth: 1, borderColor: modo === 'propria' ? cor : C.line, gap: 4 }}>
        <Text style={{ color: C.text, fontWeight: '800' }}>{modo === 'propria' ? '◉' : '○'} {t('Uma senha própria, só do diário')}</Text>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('O diário só abre com essa senha, sempre.')}</Text>
      </Pressable>
      {modo === 'propria'
        ? <CriarSenhaDiario cor={cor} botao={t('Criar o meu diário')} onPronta={(sn, tp) => criar(sn, tp)} />
        : busy ? <ActivityIndicator color={cor} /> : <Btn cor={cor} label={t('Criar o meu diário')} onPress={() => criar()} />}
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Ao criar, o app mostra um código de recuperação para você guardar.')}</Text>
      {!!msg && <Text style={{ color: C.danger }}>{msg}</Text>}
    </View>
  );
  else if (tela === 'codigo') corpo = (
    <View style={{ gap: 10 }}>
      {!!msg && <Text style={{ color: msg.includes(t('incorreto')) ? C.danger : C.sub }}>{msg}</Text>}
      <Field value={s1} onChangeText={setS1} placeholder={t('Código de recuperação (ex.: ABCD-EFGH-...)')} autoCapitalize="characters" autoCorrect={false} onSubmitEditing={pelaRecuperacao} />
      {busy ? <ActivityIndicator color={cor} /> : <Btn cor={cor} label={t('Abrir com o código')} onPress={pelaRecuperacao} disabled={!s1.trim()} />}
      {temSenhaDiario() && <Pressable onPress={() => { setTela('senha'); setMsg(''); setS1(''); }} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.sub }}>{t('Usar a senha do diário')}</Text></Pressable>}
      <Pressable onPress={apagar} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.danger }}>{t('Apagar o diário e começar de novo')}</Text></Pressable>
    </View>
  );
  else if (tela === 'senha') corpo = (
    <View style={{ gap: 10 }}>
      {!!msg && tipo !== 'padrao' && <Text style={{ color: msg.includes(t('incorreta')) ? C.danger : C.sub }}>{msg}</Text>}
      <CampoSenhaDiario tipo={tipo} cor={cor} valor={s1} onValor={setS1} erro={errou} onPronto={abrir}
        ph={tipo === 'padrao' ? (msg || t('Desenhe o padrão do diário')) : tipo === 'pin' ? t('PIN do diário') : t('Senha do diário')} />
      {tipo !== 'padrao' && (busy ? <ActivityIndicator color={cor} /> : <Btn cor={cor} label={t('Abrir o diário')} onPress={() => abrir()} />)}
      {dp.modo === 'app' && Platform.OS !== 'web' && <Pressable onPress={() => { setTela('aparelho'); peloApp(); }} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.sub }}>{t('Usar o desbloqueio do app')}</Text></Pressable>}
      <Pressable onPress={esqueci} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.sub }}>{t('Esqueci a senha do diário')}</Text></Pressable>
    </View>
  );
  else corpo = (
    <View style={{ gap: 10 }}>
      {!!msg && <Text style={{ color: C.sub }}>{msg}</Text>}
      <Btn cor={cor} label={'🔓 ' + t('Abrir o diário')} onPress={peloApp} />
      {temSenhaDiario()
        ? <Pressable onPress={() => { setTela('senha'); setMsg(''); }} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.sub }}>{t('Usar a senha do diário')}</Text></Pressable>
        : <Pressable onPress={() => { setTela('codigo'); setMsg(''); }} style={{ padding: 8, alignItems: 'center' }}><Text style={{ color: C.sub }}>{t('Usar o código de recuperação')}</Text></Pressable>}
    </View>
  );
  return (
    <Screen title={t('Diário')} onBack={onBack}>
      <View style={{ alignItems: 'center', gap: 6, marginVertical: 10 }}>
        <Text style={{ fontSize: 54 }}>📔</Text>
        <Text style={{ color: C.text, fontWeight: '800', fontSize: 18 }}>{configurado ? t('Diário protegido') : t('Seu diário, só seu')}</Text>
      </View>
      {corpo}
      <Pressable onPress={onGrupo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 14, marginTop: 16, borderWidth: 1, borderColor: C.line }}>
        <Text style={{ fontSize: 22 }}>🤝</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>{t('Projetos em Grupo')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Não pede a senha do diário. É a única parte que pode ser compartilhada.')}</Text>
        </View>
        <Text style={{ color: cor, fontWeight: '800' }}>›</Text>
      </Pressable>
      {ident.tela}
    </Screen>
  );
}

function DiarioAberto({ cor, item, onBack, onGrupo, travado }: { cor: string; item?: string; onBack: () => void; onGrupo: () => void; travado?: boolean }) {
  const dp = useDoc('diariop');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const [parte, setParte] = useState<Parte | null>(null);
  const [abrirId, setAbrirId] = useState<string | null>(null);
  const [metaInsp, setMetaInsp] = useState<string | null>(null);
  const [diaIni, setDiaIni] = useState<string | undefined>(undefined);
  const [config, setConfig] = useState(false);

  // veio de outra aba (título enviado, carta no My Planner): abre o item certo
  useEffect(() => {
    if (!item) return;
    const [p, id] = [item.slice(0, item.indexOf(':')), item.slice(item.indexOf(':') + 1)];
    const mapa: Record<string, Parte> = { meta: 'vida', hab: 'habitos', insp: 'insp', carta: 'cartas', dia: 'dia', rel: 'conquistas' };
    if (p === 'dia') setDiaIni(id);
    setAbrirId(id); setParte(mapa[p] ?? null);
  }, [item]);

  const voltar = () => { descarregar(); setParte(null); setAbrirId(null); setMetaInsp(null); setDiaIni(undefined); };
  const foco = useIsFocused();   // 0.70 (texto 09, E4): só com o Diário na tela
  useEffect(() => {
    if (!parte || travado || !foco) return;
    const s = BackHandler.addEventListener('hardwareBackPress', () => { voltar(); return true; });
    return () => s.remove();
  }, [parte, travado, foco]);
  // 11d F: na página inicial do Diário, o + abre a página do dia de hoje (cada parte aberta diz o que o + cria)
  useBotaoNovo(!parte && !travado ? { rotulo: t('Nova página do dia'), fn: () => { setDiaIni(today()); setParte('dia'); } } : null);

  if (parte === 'dia') return <MeuDia data={diaIni} cor={cor} onBack={voltar} />;
  if (parte === 'vida') return <ProjetoVida cor={cor} abrirId={abrirId} onBack={voltar} onInsp={id => { setMetaInsp(id); setParte('insp'); }} />;
  if (parte === 'habitos') return <Habitos cor={cor} abrirId={abrirId} onBack={voltar} />;
  if (parte === 'sonhos') return <QuadroSonhos cor={cor} onBack={voltar} />;
  if (parte === 'roda') return <RodaVida cor={cor} onBack={voltar} />;
  if (parte === 'cartas') return <Cartas cor={cor} abrirId={abrirId} onBack={voltar} />;
  if (parte === 'insp') return <Inspiracao cor={cor} metaNova={metaInsp} abrirId={abrirId} onBack={voltar} />;
  if (parte === 'conquistas') return <Conquistas cor={cor} onBack={voltar} />;

  // página inicial: Meu Dia recolhido no topo e as pastas abaixo
  const ordem = dp.ordem?.length ? dp.ordem : PASTAS.map(p => p.id);
  const pastas = [...PASTAS].sort((a, b) => (ordem.indexOf(a.id) + 1 || 99) - (ordem.indexOf(b.id) + 1 || 99));
  const arq = dp.arquivadas ?? [];
  const ativas = pastas.filter(p => !arq.includes(p.id));
  const arquivadas = pastas.filter(p => arq.includes(p.id));
  return (
    <Screen title={t('Diário')} onBack={onBack} right={
      <Pressable onPress={() => setConfig(true)} accessibilityLabel={t('Configurar o Diário')} style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: 18 }}>⚙️</Text></Pressable>}>
      <Pressable onPress={() => setParte('dia')} style={{ flexDirection: 'row', alignItems: 'center', gap: cx.gap(12), backgroundColor: alpha(cor, 0.14), borderRadius: cx.raio(16), ...cx.pads(14), borderWidth: 1, borderColor: alpha(cor, 0.45) }}>
        <Text style={{ fontSize: cx.icone(26) }}>📖</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{t('Meu Dia')}</Text>
          <Text style={{ color: C.sub, fontSize: 13 }}>{dataLonga(today())}</Text>
        </View>
        <Text style={{ color: cor, fontSize: 18 }}>▸</Text>
      </Pressable>
      <Ordenavel itens={ativas} chave={p => p.id} onOrdem={l => mudarDiario(d => ({ ...d, ordem: [...l.map(p => p.id as string), ...arq] }))} render={p => (
        <AcoesItem nome={t(p.nome)} onArquivar={() => {
          mudarDiario(d => ({ ...d, arquivadas: [...(d.arquivadas ?? []), p.id] }));
          // texto 05 C2: "📦 Item arquivado · Desfazer"
          oferecerDesfazer('arquivar', () => mudarDiario(d => ({ ...d, arquivadas: (d.arquivadas ?? []).filter(x => x !== p.id) })));
        }}>
          <Pressable onPress={() => (p.id === 'grupo' ? onGrupo() : setParte(p.id))} style={{ flexDirection: 'row', alignItems: 'center', gap: cx.gap(12), backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(14), borderWidth: 1, borderColor: C.line }}>
            <Text style={{ fontSize: cx.icone(26) }}>📁</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '800' }}>{p.icone} {t(p.nome)}</Text>
              {cx.resumo && <Text style={{ color: C.sub, fontSize: 12 }}>{t(p.desc)}</Text>}
            </View>
            <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
          </Pressable>
        </AcoesItem>
      )} />
      {!!arquivadas.length && <PastasArquivadas lista={arquivadas} cor={cor} />}
      <Text style={{ color: C.mute, fontSize: 11, marginTop: 8 }}>{t('Segure e arraste as pastas para mudar a ordem. Arraste para a esquerda para arquivar.')}</Text>
      <Sheet visible={config} onClose={() => setConfig(false)} title={t('Configurar o Diário')}>
        <Corpo><ConfigDiario cor={cor} /></Corpo>
      </Sheet>
    </Screen>
  );
}

function PastasArquivadas({ lista, cor }: { lista: typeof PASTAS; cor: string }) {
  const [aberto, setAberto] = useState(false);
  const cx = useCaixas();
  return (
    <View style={{ gap: cx.gap(6), marginTop: 8 }}>
      <Pressable onPress={() => setAberto(a => !a)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 4 }}>
        <Text style={{ color: C.sub }}>{aberto ? '▾' : '▸'}</Text>
        <Text style={{ color: C.sub, fontSize: 13, flex: 1 }}>📦 {t('Arquivados')} ({lista.length})</Text>
      </Pressable>
      {aberto && lista.map(p => (
        <View key={p.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), opacity: 0.85 }}>
          <Text style={{ color: C.text, flex: 1 }}>{p.icone} {t(p.nome)}</Text>
          <Chip small cor={cor} on label={'↩︎ ' + t('Restaurar')} onPress={() => mudarDiario(d => ({ ...d, arquivadas: (d.arquivadas ?? []).filter(x => x !== p.id) }))} />
        </View>
      ))}
    </View>
  );
}
